export interface MelaBrandColors {
  primary: string;
  secondary: string;
}

export type MelaThemeTokens = Record<string, string>;

const WHITE = "#ffffff";
const BLACK = "#0f172a";
const DEFAULT_PRIMARY = "#04a6f4";
const DEFAULT_SECONDARY = "#3f3d56";

export const DEFAULT_MELA_BRAND_COLORS: MelaBrandColors = {
  primary: DEFAULT_PRIMARY,
  secondary: DEFAULT_SECONDARY,
};

const isValidHexColor = (value: string) =>
  /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value);

const normalizeHexColor = (value: string, fallback: string) => {
  if (!isValidHexColor(value)) {
    return fallback.toLowerCase();
  }

  if (value.length === 4) {
    return `#${value
      .slice(1)
      .split("")
      .map((char) => `${char}${char}`)
      .join("")}`.toLowerCase();
  }

  return value.toLowerCase();
};

const hexToRgb = (value: string) => {
  const normalized = normalizeHexColor(
    value,
    DEFAULT_MELA_BRAND_COLORS.primary,
  );
  const parsed = normalized.slice(1);

  return {
    r: Number.parseInt(parsed.slice(0, 2), 16),
    g: Number.parseInt(parsed.slice(2, 4), 16),
    b: Number.parseInt(parsed.slice(4, 6), 16),
  };
};

const toHex = (value: number) =>
  Math.round(value).toString(16).padStart(2, "0");

const mixColors = (from: string, to: string, ratio: number) => {
  const safeRatio = Math.max(0, Math.min(1, ratio));
  const fromRgb = hexToRgb(from);
  const toRgb = hexToRgb(to);

  return `#${toHex(fromRgb.r + (toRgb.r - fromRgb.r) * safeRatio)}${toHex(
    fromRgb.g + (toRgb.g - fromRgb.g) * safeRatio,
  )}${toHex(fromRgb.b + (toRgb.b - fromRgb.b) * safeRatio)}`;
};

const withAlpha = (color: string, alpha: number) => {
  const safeAlpha = Math.max(0, Math.min(1, alpha));
  const { r, g, b } = hexToRgb(color);

  return `rgba(${r}, ${g}, ${b}, ${safeAlpha})`;
};

const getRelativeLuminance = (color: string) => {
  const { r, g, b } = hexToRgb(color);
  const channels = [r, g, b].map((channel) => {
    const normalized = channel / 255;

    return normalized <= 0.03928
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4;
  });

  return channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
};

const getContrastText = (color: string) =>
  getRelativeLuminance(color) > 0.54 ? BLACK : WHITE;

const buildBrandToneTokens = (
  toneName: "primary" | "secondary",
  color: string,
): MelaThemeTokens => {
  const tone = normalizeHexColor(color, DEFAULT_MELA_BRAND_COLORS[toneName]);
  const solidHover = mixColors(tone, BLACK, 0.18);

  return {
    [`--mela-color-${toneName}`]: tone,
    [`--mela-color-${toneName}-soft-bg`]: mixColors(tone, WHITE, 0.9),
    [`--mela-color-${toneName}-soft-border`]: mixColors(tone, WHITE, 0.58),
    [`--mela-color-${toneName}-soft-text`]: mixColors(tone, BLACK, 0.22),
    [`--mela-color-${toneName}-soft-hover-bg`]: mixColors(tone, WHITE, 0.84),
    [`--mela-color-${toneName}-soft-hover-border`]: mixColors(
      tone,
      WHITE,
      0.42,
    ),
    [`--mela-color-${toneName}-soft-hover-text`]: mixColors(tone, BLACK, 0.22),
    [`--mela-color-${toneName}-solid-bg`]: tone,
    [`--mela-color-${toneName}-solid-border`]: tone,
    [`--mela-color-${toneName}-solid-text`]: getContrastText(tone),
    [`--mela-color-${toneName}-solid-hover-bg`]: solidHover,
    [`--mela-color-${toneName}-solid-hover-border`]: solidHover,
    [`--mela-color-${toneName}-solid-hover-text`]: getContrastText(tone),
    [`--mela-color-${toneName}-solid-shadow`]: `0 1px 2px ${withAlpha(
      solidHover,
      0.18,
    )}`,
    [`--mela-color-${toneName}-solid-spinner-track`]: withAlpha(tone, 0.18),
    [`--mela-color-${toneName}-ghost-text`]: tone,
    [`--mela-color-${toneName}-ghost-hover-text`]: mixColors(tone, BLACK, 0.18),
    [`--mela-color-${toneName}-spinner`]: mixColors(tone, BLACK, 0.22),
    [`--mela-color-${toneName}-spinner-track`]: withAlpha(tone, 0.18),
    [`--mela-color-${toneName}-focus-ring`]: withAlpha(tone, 0.2),
  };
};

const FIXED_TOKENS: MelaThemeTokens = {
  "--mela-color-ghost-hover-bg": "rgba(15, 23, 42, 0.04)",
  "--mela-surface-page": "#f8fafc",
  "--mela-surface-card": "#ffffff",
  "--mela-surface-subtle": "#f8fafc",
  "--mela-surface-muted": "#f1f5f9",
  "--mela-surface-elevated": "#ffffff",
  "--mela-surface-inverse": "#334155",
  "--mela-text-primary": "#1f2937",
  "--mela-text-secondary": "#4b5563",
  "--mela-text-muted": "#64748b",
  "--mela-text-inverse": "#ffffff",
  "--mela-border-subtle": "#e2e8f0",
  "--mela-border-default": "#cbd5e1",
  "--mela-border-strong": "#94a3b8",
  "--mela-shadow-sm": `0 1px 2px ${withAlpha(BLACK, 0.08)}`,
  "--mela-shadow-md": `0 10px 15px -3px ${withAlpha(BLACK, 0.1)}, 0 4px 6px -2px ${withAlpha(BLACK, 0.05)}`,
  "--mela-shadow-lg": `0 16px 32px -8px ${withAlpha(BLACK, 0.18)}, 0 6px 12px -4px ${withAlpha(BLACK, 0.12)}`,
  "--mela-overlay": withAlpha(BLACK, 0.48),
  "--mela-focus-ring-neutral": withAlpha("#334155", 0.2),
  "--mela-color-neutral-soft-bg": "#f8fafc",
  "--mela-color-neutral-soft-border": "#cbd5e1",
  "--mela-color-neutral-soft-text": "#334155",
  "--mela-color-neutral-soft-hover-bg": "#f1f5f9",
  "--mela-color-neutral-soft-hover-border": "#94a3b8",
  "--mela-color-neutral-soft-hover-text": "#334155",
  "--mela-color-neutral-focus-ring": "rgba(51, 65, 85, 0.2)",
  "--mela-color-neutral-solid-bg": "#334155",
  "--mela-color-neutral-solid-border": "#334155",
  "--mela-color-neutral-solid-text": "#ffffff",
  "--mela-color-neutral-solid-hover-bg": "#1e293b",
  "--mela-color-neutral-solid-hover-border": "#1e293b",
  "--mela-color-neutral-solid-hover-text": "#ffffff",
  "--mela-color-neutral-solid-spinner-track": "rgba(255, 255, 255, 0.35)",
  "--mela-color-neutral-ghost-text": "#334155",
  "--mela-color-neutral-ghost-hover-text": "#334155",
  "--mela-color-neutral-spinner": "#334155",
  "--mela-color-neutral-spinner-track": "rgba(51, 65, 85, 0.2)",
  "--mela-color-success-soft-bg": "#dcfce7",
  "--mela-color-success-subtle-bg": "#f0fdf4",
  "--mela-color-success-soft-border": "#86efac",
  "--mela-color-success-soft-text": "#166534",
  "--mela-color-success-soft-hover-bg": "#bbf7d0",
  "--mela-color-success-soft-hover-border": "#4ade80",
  "--mela-color-success-soft-hover-text": "#166534",
  "--mela-color-success-solid-bg": "#166534",
  "--mela-color-success-solid-border": "#166534",
  "--mela-color-success-solid-text": "#ffffff",
  "--mela-color-success-spinner": "#166534",
  "--mela-color-success-spinner-track": "rgba(22, 101, 52, 0.18)",
  "--mela-color-success-focus-ring": "rgba(22, 101, 52, 0.2)",
  "--mela-color-warning-soft-bg": "#fff7ed",
  "--mela-color-warning-subtle-bg": "#fffbeb",
  "--mela-color-warning-soft-border": "#fdba74",
  "--mela-color-warning-soft-text": "#c2410c",
  "--mela-color-warning-soft-hover-bg": "#ffedd5",
  "--mela-color-warning-soft-hover-border": "#fb923c",
  "--mela-color-warning-soft-hover-text": "#c2410c",
  "--mela-color-warning-solid-bg": "#c2410c",
  "--mela-color-warning-solid-border": "#c2410c",
  "--mela-color-warning-solid-text": "#ffffff",
  "--mela-color-warning-spinner": "#c2410c",
  "--mela-color-warning-spinner-track": "rgba(194, 65, 12, 0.18)",
  "--mela-color-warning-focus-ring": "rgba(194, 65, 12, 0.2)",
  "--mela-color-danger-soft-bg": "#fef2f2",
  "--mela-color-danger-subtle-bg": "#fff5f5",
  "--mela-color-danger-soft-border": "#fecaca",
  "--mela-color-danger-soft-text": "#dc2626",
  "--mela-color-danger-soft-hover-bg": "#fee2e2",
  "--mela-color-danger-soft-hover-border": "#fca5a5",
  "--mela-color-danger-soft-hover-text": "#dc2626",
  "--mela-color-danger-solid-bg": "#dc2626",
  "--mela-color-danger-solid-border": "#dc2626",
  "--mela-color-danger-solid-text": "#ffffff",
  "--mela-color-danger-spinner": "#dc2626",
  "--mela-color-danger-spinner-track": "rgba(220, 38, 38, 0.18)",
  "--mela-color-danger-focus-ring": "rgba(220, 38, 38, 0.2)",
  "--mela-color-ai-soft-bg": "#f5f3ff",
  "--mela-color-ai-soft-border": "#c4b5fd",
  "--mela-color-ai-soft-text": "#6d28d9",
  "--mela-color-ai-soft-hover-bg": "#ede9fe",
  "--mela-color-ai-soft-hover-border": "#a78bfa",
  "--mela-color-ai-soft-hover-text": "#6d28d9",
  "--mela-color-ai-solid-bg": "#6d28d9",
  "--mela-color-ai-solid-border": "#6d28d9",
  "--mela-color-ai-solid-text": "#ffffff",
  "--mela-color-ai-spinner": "#6d28d9",
  "--mela-color-ai-spinner-track": "rgba(109, 40, 217, 0.18)",
  "--mela-color-ai-focus-ring": "rgba(109, 40, 217, 0.2)",
  "--mela-color-tooltip-bg": withAlpha("#334155", 0.88),
  "--mela-color-tooltip-text": WHITE,
  "--mela-color-tooltip-border": withAlpha(WHITE, 0.12),
  "--mela-color-tooltip-shadow": `0 12px 30px ${withAlpha(BLACK, 0.18)}`,
};

export const buildMelaThemeTokens = (
  brandColors: MelaBrandColors = DEFAULT_MELA_BRAND_COLORS,
): MelaThemeTokens => ({
  ...FIXED_TOKENS,
  ...buildBrandToneTokens("primary", brandColors.primary),
  ...buildBrandToneTokens("secondary", brandColors.secondary),
});

export const applyMelaThemeBranding = (
  brandColors: MelaBrandColors = DEFAULT_MELA_BRAND_COLORS,
  root: HTMLElement = document.documentElement,
) => {
  const tokens = buildMelaThemeTokens(brandColors);

  Object.entries(tokens).forEach(([tokenName, tokenValue]) => {
    root.style.setProperty(tokenName, tokenValue);
  });

  return tokens;
};
